<app-split-pane
  storageKey="split.quotations"
  [initialLeftRatio]="0.7"
  [collapseBreakpointPx]="1024"
  [collapsedOnSmallScreens]="true"
  >
  <ng-container left>
    <app-split-pane-vertical storageKey="split.quotations.left" [initialTopRatio]="0.7">
      <ng-container top>
        <app-chat
          #quotationsChat
          [conversationId]="rootStore.selectedProjectId()"
          [suggestions]="quotationsSuggestions"
          [context]="quotationsContext"
          [orchestratorPageContext]="quotationsOrchestratorContext"
          [initialMessages]="chatMessages"
          (messagesChange)="onChatMessagesChange($event)"
          (messageCreated)="onChatMessageCreated($event)"
          (aiResult)="onAiResult($event)"
          placeholder="Start describing your quotation..."
        ></app-chat>
      </ng-container>
      <ng-container bottom>
        <app-history scopeKey="quotations.chat"></app-history>
      </ng-container>
    </app-split-pane-vertical>
  </ng-container>

  <ng-container right>
    <div class="container d-flex flex-column h-100">
      <ul class="nav nav-tabs">
        <li class="nav-item">
          <button type="button" class="nav-link" [class.active]="activeTab === 'templates'" (click)="setTab('templates')">Drafts</button>
        </li>
        <li class="nav-item">
          <button type="button" class="nav-link" [class.active]="activeTab === 'active'" (click)="setTab('active')">Active Quotations</button>
        </li>
        <li class="nav-item">
          <button type="button" class="nav-link" [class.active]="activeTab === 'history'" (click)="setTab('history')">Quoting History</button>
        </li>
      </ul>

      <div class="pt-3 flex-grow-1 min-h-0 d-flex">
        @if (activeTab === 'templates') {
          <app-inner-split-pane storageKey="split.quotations.templates.inner" [initialLeftRatio]="0.5">
            <ng-container left>
              <app-split-pane-vertical storageKey="split.quotations.templates.left" [initialTopRatio]="0.7">
                <ng-container top>
                  <div class="d-flex flex-column gap-3 mx-1 h-100 min-h-0 drafts-pane-shell">
                    <div class="card p-3 d-flex flex-column gap-3 drafts-list-card">
                      @if (isQuotationsLoading) {
                        <div class="quotation-status-loading small">
                          <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Loading quotation artifacts...
                        </div>
                      } @else {
                        <div class="card p-0 overflow-auto generated-list-table-wrap">
                          <table class="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Title</th>
                                <th>Updated</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (draft of pendingApprovalQuotes; track draft.id) {
                                <tr
                                  class="clickable"
                                  (click)="selectDraftQuote(draft)"
                                  [class.selected]="selectedDraftQuote?.id === draft.id"
                                >
                                  <td class="generated-name">
                                    <span class="generated-name-text">{{ draft.generatedName }}</span>
                                  </td>
                                  <td class="text-nowrap">{{ draft.updatedAt | date: 'short' }}</td>
                                  <td class="text-nowrap" (click)="$event.stopPropagation()">
                                    <div class="generated-row-actions">
                                      <button
                                        type="button"
                                        class="template-row-icon-btn template-row-refresh"
                                        title="Open draft"
                                        (click)="openExternalQuotation(draft.previewUrl || draft.webViewLink)"
                                      >
                                        <i class="bi bi-box-arrow-up-right"></i>
                                      </button>
                                      <button
                                        type="button"
                                        class="template-row-icon-btn template-delete-btn"
                                        title="Delete draft"
                                        (click)="openDeleteQuoteModal(draft)"
                                        [disabled]="isDeletingQuote"
                                      >
                                        <i class="bi bi-trash"></i>
                                      </button>
                                      @if (draft.status === 'draft') {
                                        <button
                                          class="btn btn-success btn-sm generated-approve-btn"
                                          (click)="approveDraftQuote(draft)"
                                          [disabled]="isUpdatingQuote"
                                        >
                                          Approve
                                        </button>
                                      } @else {
                                        <span class="badge text-bg-success">Approved</span>
                                      }
                                    </div>
                                  </td>
                                </tr>
                              }
                              @if (!pendingApprovalQuotes.length) {
                                <tr>
                                  <td colspan="3" class="py-3 text-center generated-empty-message">
                                    No quotation drafts yet. Ask the chat to draft a quotation for this project.
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>

                    <div class="card p-3 d-flex flex-column gap-2 draft-editor-card">
                      @if (selectedDraftQuote; as pendingQuote) {
                        <div class="d-flex align-items-center justify-content-between gap-2 mt-1 flex-wrap">
                          <div class="small pending-draft-name">Editing draft - {{ pendingQuote.generatedName }}</div>
                        </div>

                        <div class="d-flex flex-column gap-2 mt-2 draft-editor-scroll">
                          <label class="form-label small mb-0">Draft title</label>
                          <input
                            class="form-control form-control-sm"
                            [ngModel]="draftEditorName"
                            (ngModelChange)="draftEditorName = $event"
                            placeholder="Quotation title"
                          />

                          <label class="form-label small mb-0 mt-1">Executive summary</label>
                          <textarea
                            class="form-control form-control-sm"
                            rows="3"
                            [ngModel]="draftEditorPlaceholders['executive_summary'] || ''"
                            (ngModelChange)="updateDraftField('executive_summary', $event)"
                            placeholder="Summarize the engagement and its business outcome"
                          ></textarea>

                          <label class="form-label small mb-0 mt-1">Scope of Work</label>
                          <textarea
                            class="form-control form-control-sm"
                            rows="4"
                            [ngModel]="draftEditorPlaceholders['scope_of_work'] || ''"
                            (ngModelChange)="updateDraftField('scope_of_work', $event)"
                            placeholder="Describe scope of work"
                          ></textarea>

                          <label class="form-label small mb-0 mt-1">Deliverables</label>
                          <textarea
                            class="form-control form-control-sm"
                            rows="4"
                            [ngModel]="draftEditorPlaceholders['deliverables'] || ''"
                            (ngModelChange)="updateDraftField('deliverables', $event)"
                            placeholder="List deliverables"
                          ></textarea>

                          <label class="form-label small mb-0 mt-1">Milestones and timeline</label>
                          <input
                            class="form-control form-control-sm"
                            [ngModel]="draftEditorPlaceholders['timeline'] || ''"
                            (ngModelChange)="updateDraftField('timeline', $event)"
                            placeholder="10-12 weeks"
                          />
                          <textarea
                            class="form-control form-control-sm"
                            rows="3"
                            [ngModel]="draftEditorPlaceholders['milestones'] || ''"
                            (ngModelChange)="updateDraftField('milestones', $event)"
                            placeholder="Discovery, Build, QA, Launch"
                          ></textarea>

                          <label class="form-label small mb-0 mt-1">Assumptions</label>
                          <textarea
                            class="form-control form-control-sm"
                            rows="3"
                            [ngModel]="draftEditorPlaceholders['assumptions'] || ''"
                            (ngModelChange)="updateDraftField('assumptions', $event)"
                            placeholder="Client provides assets, feedback within two working days, etc."
                          ></textarea>

                          <label class="form-label small mb-0 mt-1">Exclusions</label>
                          <textarea
                            class="form-control form-control-sm"
                            rows="3"
                            [ngModel]="draftEditorPlaceholders['exclusions'] || ''"
                            (ngModelChange)="updateDraftField('exclusions', $event)"
                            placeholder="Items outside the current quotation scope"
                          ></textarea>

                          <label class="form-label small mb-0 mt-1">Commercial line items</label>
                          <div class="pricing-editor-card">
                            <div class="d-flex align-items-center justify-content-between gap-2 flex-wrap pricing-editor-toolbar">
                              <div class="small text-muted pricing-editor-note">
                                Add pricing line items and the quotation total will update automatically.
                              </div>
                              <button class="btn btn-outline-primary btn-sm" type="button" (click)="addPricingRow()">
                                Add line item
                              </button>
                            </div>

                            @if (!draftPricingRows.length) {
                              <div class="pricing-editor-empty small">
                                No line items added yet. Add a row to build the customer pricing table.
                              </div>
                            }

                            <div class="d-flex flex-column gap-2 pricing-editor-rows">
                              @for (row of draftPricingRows; track $index; let i = $index) {
                                <div class="pricing-row">
                                  <div class="pricing-row-grid">
                                    <div class="pricing-row-field">
                                      <label class="form-label small mb-0">Label</label>
                                      <input
                                        class="form-control form-control-sm"
                                        [ngModel]="row.label"
                                        (ngModelChange)="updatePricingRow(i, 'label', $event)"
                                        placeholder="Discovery"
                                      />
                                    </div>
                                    <div class="pricing-row-field">
                                      <label class="form-label small mb-0">Description</label>
                                      <input
                                        class="form-control form-control-sm"
                                        [ngModel]="row.description"
                                        (ngModelChange)="updatePricingRow(i, 'description', $event)"
                                        placeholder="Requirements workshop and solution definition"
                                      />
                                    </div>
                                    <div class="pricing-row-field pricing-row-amount">
                                      <label class="form-label small mb-0">Amount</label>
                                      <input
                                        class="form-control form-control-sm"
                                        [ngModel]="row.amount"
                                        (ngModelChange)="updatePricingRow(i, 'amount', $event)"
                                        placeholder="6000"
                                      />
                                    </div>
                                  </div>
                                  <div class="d-flex justify-content-end mt-2">
                                    <button class="btn btn-outline-danger btn-sm" type="button" (click)="removePricingRow(i)">
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              }
                            </div>

                            <div class="pricing-editor-summary">
                              <div class="small">
                                <span class="text-muted">Calculated total:</span>
                                <strong>{{ draftCalculatedTotalAmount || 'Add valid amounts to calculate a total' }}</strong>
                              </div>
                              @if (hasCalculatedDraftPricingTotal && isUsingCalculatedDraftTotal) {
                                <span class="badge text-bg-success">Auto total active</span>
                              }
                              @if (hasCalculatedDraftPricingTotal && isTotalAmountManualOverride) {
                                <div class="d-flex align-items-center gap-2 flex-wrap">
                                  <span class="badge text-bg-warning">Manual total override active</span>
                                  <button class="btn btn-outline-secondary btn-sm" type="button" (click)="useCalculatedTotalAmount()">
                                    Use calculated total
                                  </button>
                                </div>
                              }
                            </div>
                          </div>

                          <div class="d-flex gap-2">
                            <div class="flex-fill">
                              <label class="form-label small mb-0 mt-1">Total amount</label>
                              <input
                                class="form-control form-control-sm"
                                [ngModel]="draftEditorPlaceholders['total_amount'] || ''"
                                (ngModelChange)="updateTotalAmount($event)"
                                placeholder="$0.00"
                              />
                            </div>
                            <div class="flex-fill">
                              <label class="form-label small mb-0 mt-1">Currency</label>
                              <input
                                class="form-control form-control-sm"
                                [ngModel]="draftEditorPlaceholders['currency'] || ''"
                                (ngModelChange)="updateDraftField('currency', $event)"
                                placeholder="USD"
                              />
                            </div>
                          </div>

                          <label class="form-label small mb-0 mt-1">Payment terms</label>
                          <input
                            class="form-control form-control-sm"
                            [ngModel]="draftEditorPlaceholders['payment_terms'] || ''"
                            (ngModelChange)="updateDraftField('payment_terms', $event)"
                            placeholder="50% upfront, 50% on delivery"
                          />

                          <label class="form-label small mb-0 mt-1">Next steps</label>
                          <textarea
                            class="form-control form-control-sm"
                            rows="3"
                            [ngModel]="draftEditorPlaceholders['next_steps'] || ''"
                            (ngModelChange)="updateDraftField('next_steps', $event)"
                            placeholder="Explain approval and kickoff steps"
                          ></textarea>

                          <div class="d-flex justify-content-end mt-2">
                            <button class="btn btn-outline-primary btn-sm" (click)="saveSelectedDraftEdits()" [disabled]="isSavingDraftEdits">
                              @if (isSavingDraftEdits) {
                                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              }
                              Save draft changes
                            </button>
                          </div>
                        </div>
                      } @else {
                        <div class="small pending-draft-name mt-1 draft-editor-empty-state">Select a draft to edit section content.</div>
                      }
                    </div>

                  </div>
                </ng-container>
                <ng-container bottom>
                  <app-history scopeKey="quotations.templates"></app-history>
                </ng-container>
              </app-split-pane-vertical>
            </ng-container>
            <ng-container right>
              <div class="card p-0 h-100 ms-1 d-flex flex-column">
                <div class="p-3 d-flex flex-column gap-3 h-100">
                  @if (selectedDraftQuote) {
                    @if (selectedTemplatePreviewUrl) {
                      <div class="template-preview-wrapper">
                        @if (isTemplatePreviewLoading) {
                          <div class="template-preview-overlay small">
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading draft preview...
                          </div>
                        }
                        @if (hasTemplatePreviewError) {
                          <div class="template-preview-overlay template-preview-overlay-error small">
                            <div class="d-flex flex-column align-items-center gap-2 px-3 text-center">
                              <div>Embedded preview failed to load.</div>
                              <div class="small text-muted">Generate a draft to populate the preview.</div>
                            </div>
                          </div>
                        }
                        <iframe
                          class="template-preview-frame"
                          [src]="selectedTemplatePreviewUrl"
                          title="Selected draft preview"
                          loading="lazy"
                          allowfullscreen
                          (load)="onTemplatePreviewLoaded()"
                          (error)="onTemplatePreviewError()"
                        ></iframe>
                      </div>
                    } @else {
                      <div class="alert alert-warning mb-0">
                        Preview unavailable. Generate a draft quotation first.
                      </div>
                    }
                  } @else {
                    <div>Generate and select a draft quotation to start previewing</div>
                  }
                </div>
              </div>
            </ng-container>
          </app-inner-split-pane>
        }
        @if (activeTab === 'active') {
          <app-inner-split-pane storageKey="split.quotations.active.inner" [initialLeftRatio]="0.55">
            <ng-container left>
              <app-split-pane-vertical storageKey="split.quotations.active.left" [initialTopRatio]="0.7">
                <ng-container top>
                  <div class="d-flex flex-column gap-3 mx-1">
                    <div class="card p-0 overflow-auto">
                      <table class="table table-hover mb-0">
                        <thead>
                          <tr>
                            <th>Quote #</th>
                            <th>Client</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Updated</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (q of activeQuotes; track q.id) {
                            <tr class="clickable" (click)="selectActiveQuote(q)" [class.selected]="selectedActiveQuote?.id === q.id">
                              <td>{{ q.id }}</td>
                              <td>{{ q.placeholders['client_name'] || '-' }}</td>
                              <td>{{ q.placeholders['total_amount'] || '-' }}</td>
                              <td>
                                <span class="quote-status-pill" [ngClass]="statusPillClass(q.status)">
                                  {{ statusLabel(q.status) }}
                                </span>
                              </td>
                              <td>{{ q.updatedAt | date: 'short' }}</td>
                              <td class="text-nowrap" (click)="$event.stopPropagation()">
                                <button type="button" class="btn btn-sm btn-outline-success" title="Send quotation email" data-bs-toggle="tooltip" data-bs-placement="top" (click)="selectActiveQuote(q)">
                                  <i class="bi bi-envelope"></i>
                                </button>
                                <button type="button" class="btn btn-sm btn-outline-danger ms-2" title="Delete quotation" data-bs-toggle="tooltip" data-bs-placement="top" (click)="openDeleteQuoteModal(q)" [disabled]="isDeletingQuote">
                                  <i class="bi bi-trash"></i>
                                </button>
                              </td>
                            </tr>
                          }
                          @if (!activeQuotes.length) {
                            <tr>
                              <td colspan="6" class="text-center py-4 quote-empty-message">
                                No approved quotations yet. Approve a draft from Drafts to move it here.
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>

                    <div class="card p-3 quote-actions-card">
                      <div class="fw-500 mb-2">Quotation Actions</div>
                      @if (selectedActiveQuote) {
                        <div class="mb-2">Viewing: {{ selectedActiveQuote.generatedName }}</div>
                        <div class="small mb-2 d-flex align-items-center gap-2">
                          <span>Status:</span>
                          <span class="quote-status-pill" [ngClass]="statusPillClass(selectedActiveQuote.status)">
                            {{ statusLabel(selectedActiveQuote.status) }}
                          </span>
                        </div>

                        <label class="form-label small mb-1">Recipient email</label>
                        <input class="form-control form-control-sm mb-2" [ngModel]="selectedQuoteRecipient" (ngModelChange)="selectedQuoteRecipient = $event" placeholder="client@example.com" />

                        <label class="form-label small mb-1">Email subject</label>
                        <input class="form-control form-control-sm mb-2" [ngModel]="selectedQuoteSubject" (ngModelChange)="selectedQuoteSubject = $event" placeholder="Quotation subject" />

                        <label class="form-label small mb-1">Email message</label>
                        <textarea class="form-control form-control-sm mb-2" rows="4" [ngModel]="selectedQuoteMessage" (ngModelChange)="selectedQuoteMessage = $event" placeholder="Optional message to include before the quote link"></textarea>

                        <div class="d-flex gap-2 flex-wrap">
                          <button class="btn btn-outline-secondary btn-sm" (click)="saveSelectedQuoteDetails()" [disabled]="isUpdatingQuote">
                            Save details
                          </button>
                          <button class="btn btn-success btn-sm" (click)="sendSelectedQuoteEmail()" [disabled]="isSendingEmail || !selectedQuoteRecipient">
                            @if (isSendingEmail) {
                              <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            }
                            Send email
                          </button>
                          <button class="btn btn-outline-success btn-sm" (click)="setSelectedQuoteStatus('accepted')" [disabled]="isUpdatingQuote">Mark accepted</button>
                          <button class="btn btn-outline-danger btn-sm" (click)="setSelectedQuoteStatus('rejected')" [disabled]="isUpdatingQuote">Mark rejected</button>
                          <button class="btn btn-outline-warning btn-sm" (click)="setSelectedQuoteStatus('expired')" [disabled]="isUpdatingQuote">Mark expired</button>
                        </div>
                      } @else {
                        <div>Select a quotation from the table to manage actions.</div>
                      }
                    </div>
                  </div>
                </ng-container>
                <ng-container bottom>
                  <app-history scopeKey="quotations.active"></app-history>
                </ng-container>
              </app-split-pane-vertical>
            </ng-container>
            <ng-container right>
              <div class="card p-0 h-100 ms-1 d-flex flex-column quote-preview-card">
                <div class="p-3 d-flex flex-column gap-3 h-100">
                  <div class="fw-500">Quotation Preview</div>
                  @if (selectedActiveQuote) {
                    @if (selectedActiveQuotePreviewUrl) {
                      <div class="template-preview-wrapper mb-2">
                        @if (isQuotePreviewLoading) {
                          <div class="template-preview-overlay small">
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading quotation preview...
                          </div>
                        }
                        @if (hasQuotePreviewError) {
                          <div class="template-preview-overlay template-preview-overlay-error small">
                            Embedded quote preview failed to load.
                          </div>
                        }
                        <iframe
                          class="template-preview-frame"
                          [src]="selectedActiveQuotePreviewUrl"
                          title="Selected quotation preview"
                          loading="lazy"
                          allowfullscreen
                          (load)="onQuotePreviewLoaded()"
                          (error)="onQuotePreviewError()"
                        ></iframe>
                      </div>
                    } @else {
                      <div class="alert alert-warning mb-0">
                        Preview unavailable. Refresh artifacts to regenerate a preview.
                      </div>
                    }
                  } @else {
                    <div>Select a quotation from the table to preview.</div>
                  }
                </div>
              </div>
            </ng-container>
          </app-inner-split-pane>
        }
        @if (activeTab === 'history') {
          <app-inner-split-pane storageKey="split.quotations.history.inner" [initialLeftRatio]="0.55">
            <ng-container left>
              <div class="d-flex flex-column mx-1 h-100">
                <div class="card p-0 overflow-auto">
                  <table class="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Quote #</th>
                        <th>Client</th>
                        <th>Amount</th>
                        <th>Sent On</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (q of historyQuotes; track q.id) {
                        <tr class="clickable" (click)="selectHistoryQuote(q)" [class.selected]="selectedHistoryQuote?.id === q.id">
                          <td>{{ q.id }}</td>
                          <td>{{ q.placeholders['client_name'] || '-' }}</td>
                          <td>{{ q.placeholders['total_amount'] || '-' }}</td>
                          <td>{{ q.updatedAt | date: 'short' }}</td>
                          <td>
                            <span class="quote-status-pill" [ngClass]="statusPillClass(q.status)">
                              {{ statusLabel(q.status) }}
                            </span>
                          </td>
                          <td class="text-nowrap" (click)="$event.stopPropagation()">
                            <button type="button" class="btn btn-sm btn-outline-secondary me-2" title="Move quotation back to Active" data-bs-toggle="tooltip" data-bs-placement="top" (click)="unarchiveHistoryQuote(q)" [disabled]="isUpdatingQuote">
                              <i class="bi bi-arrow-counterclockwise"></i>
                            </button>
                          </td>
                        </tr>
                      }
                      @if (!historyQuotes.length) {
                        <tr>
                          <td colspan="6" class="text-center py-4 quote-empty-message">
                            No quotation history yet. Sent, accepted, rejected, or expired quotations will appear here.
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </ng-container>
            <ng-container right>
              <div class="card p-0 h-100 ms-1 d-flex flex-column quote-preview-card">
                <div class="p-3 d-flex flex-column gap-3 h-100">
                  <div class="fw-500">Quotation Viewer</div>
                  @if (selectedHistoryQuote) {
                    @if (selectedHistoryQuotePreviewUrl) {
                      <div class="template-preview-wrapper">
                        @if (isHistoryPreviewLoading) {
                          <div class="template-preview-overlay small">
                            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Loading quotation preview...
                          </div>
                        }
                        @if (hasHistoryPreviewError) {
                          <div class="template-preview-overlay template-preview-overlay-error small">
                            Embedded quote preview failed to load.
                          </div>
                        }
                        <iframe
                          class="template-preview-frame"
                          [src]="selectedHistoryQuotePreviewUrl"
                          title="Selected quotation history preview"
                          loading="lazy"
                          allowfullscreen
                          (load)="onHistoryPreviewLoaded()"
                          (error)="onHistoryPreviewError()"
                        ></iframe>
                      </div>
                    } @else {
                      <div class="alert alert-warning mb-0">
                        Preview unavailable for this quotation.
                      </div>
                    }
                  } @else {
                    <div>Select a sent quotation from the table to preview.</div>
                  }
                </div>
              </div>
            </ng-container>
          </app-inner-split-pane>
        }
      </div>
    </div>
  </ng-container>
</app-split-pane>

@if (showQuotationFab) {
  <div class="quote-fab-wrap d-flex flex-column align-items-end gap-2">
    @if (quoteFabExpanded) {
      <div class="quote-fab-panel p-3">
        <div class="d-flex align-items-start justify-content-between gap-2">
          <div>
            <div class="fw-semibold">Quotation generation</div>
            <div class="small text-muted">{{ quoteFabPhaseLabel }}</div>
          </div>
          <button type="button" class="btn btn-sm btn-light" (click)="toggleQuoteFab()">Hide</button>
        </div>
        @if (quoteFabError) {
          <div class="small text-danger mt-2">{{ quoteFabError }}</div>
        }
        @if (quoteFabDeckUrl) {
          <div class="small mt-2">
            <a [href]="quoteFabDeckUrl" target="_blank" rel="noopener noreferrer">Open generated document</a>
          </div>
        }
      </div>
    }
    <button
      type="button"
      class="btn btn-primary btn-sm quote-fab-button d-flex align-items-center gap-2"
      (click)="toggleQuoteFab()"
    >
      @if (isQuotationGenerationRunning) {
        <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      } @else if (quoteFabPhase === 'completed') {
        <i class="bi bi-check-circle"></i>
      } @else if (quoteFabPhase === 'failed') {
        <i class="bi bi-exclamation-triangle"></i>
      }
      <span>
        {{
          isQuotationGenerationRunning
            ? 'Generating quote'
            : quoteFabPhase === 'completed'
              ? 'Last quote complete'
              : 'Quote status'
        }}
      </span>
    </button>
  </div>
}

<ng-template #deleteQuoteModal>
  <div class="modal-header">
    <h5 class="modal-title">Delete quotation</h5>
    <button type="button" class="btn-close" aria-label="Close" (click)="closeDeleteQuoteModal()" [disabled]="isDeletingQuote"></button>
  </div>
  <div class="modal-body">
    <p class="mb-0">
      Are you sure you want to delete
      <span class="fw-semibold">{{ pendingDeleteQuote?.generatedName }}</span>?
      This cannot be undone.
    </p>
    <div class="text-muted small mt-3">
      <div><span class="fw-semibold">Quote ID</span>: {{ pendingDeleteQuote?.id }}</div>
      <div><span class="fw-semibold">Current status</span>: {{ pendingDeleteQuote?.status }}</div>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-outline-secondary" (click)="closeDeleteQuoteModal()" [disabled]="isDeletingQuote">Cancel</button>
    <button type="button" class="btn btn-danger" (click)="confirmDeleteQuote()" [disabled]="isDeletingQuote || !pendingDeleteQuote">
      @if (isDeletingQuote) {
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
      }
      {{ isDeletingQuote ? 'Deleting...' : 'Delete' }}
    </button>
  </div>
</ng-template>
